/**
 * Vector3 - 3D Vector Class
 *
 * Represents a point or direction in 3D space.
 * In isometric games, Z is typically used for height/elevation.
 *
 * YOUR TASK: Complete the TODO sections
 *
 * TypeScript Concepts Demonstrated:
 * - Extending functionality from Vector2 concepts
 * - Static factory methods with optional parameters
 * - Type conversion between classes
 *
 * @example
 * const position = new Vector3(10, 20, 5); // x, y, z (5 units "above" ground)
 * const flatPosition = position.toVector2(); // Drops the Z component
 */

import { Vector2 } from './Vector2';

export class Vector3 {
  /**
   * Epsilon for floating-point comparisons
   */
  private static readonly EPSILON = 0.0001;

  /**
   * The X component (horizontal position in isometric grid)
   */
  public x: number;

  /**
   * The Y component (depth position in isometric grid)
   */
  public y: number;

  /**
   * The Z component (height/elevation above ground)
   */
  public z: number;

  /**
   * Create a new Vector3
   * @param x - The X component (default: 0)
   * @param y - The Y component (default: 0)
   * @param z - The Z component (default: 0)
   */
  constructor(x: number = 0, y: number = 0, z: number = 0) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  // ============================================
  // STATIC FACTORY METHODS
  // ============================================

  /**
   * Create a zero vector (0, 0, 0)
   */
  static zero(): Vector3 {
    return new Vector3(0, 0, 0);
  }

  /**
   * Create a one vector (1, 1, 1)
   */
  static one(): Vector3 {
    // TODO: Implement this method
    return new Vector3(1, 1, 1);
  }

  /**
   * Create a unit X vector (1, 0, 0)
   */
  static unitX(): Vector3 {
    return new Vector3(1, 0, 0);
  }

  /**
   * Create a unit Y vector (0, 1, 0)
   */
  static unitY(): Vector3 {
    return new Vector3(0, 1, 0);
  }

  /**
   * Create a unit Z vector (0, 0, 1) - points "up" in world space
   */
  static unitZ(): Vector3 {
    return new Vector3(0, 0, 1);
  }

  /**
   * Create a Vector3 from a Vector2
   * @param v - The 2D vector
   * @param z - The Z component (default: 0)
   * @returns A new Vector3
   *
   * This is useful for converting screen/grid positions to world positions
   */
  static fromVector2(v: Vector2, z: number = 0): Vector3 {
    // TODO: Implement this method
    return new Vector3(v.x, v.y, z);
  }

  // ============================================
  // CONVERSION METHODS
  // ============================================

  /**
   * Convert to Vector2 (drops the Z component)
   * Useful for screen-space calculations
   */
  toVector2(): Vector2 {
    // TODO: Implement this method
    return new Vector2(this.x, this.y);
  }

  // ============================================
  // ARITHMETIC METHODS
  // ============================================

  /**
   * Add another vector to this one
   */
  add(other: Vector3): Vector3 {
    // TODO: Implement this method
    return new Vector3(
      this.x + other.x,
      this.y + other.y,
      this.z + other.z
    );
  }

  /**
   * Subtract another vector from this one
   */
  subtract(other: Vector3): Vector3 {
    // TODO: Implement this method
    return new Vector3(
      this.x - other.x,
      this.y - other.y,
      this.z - other.z
    );
  }

  /**
   * Multiply this vector by a scalar
   */
  multiply(scalar: number): Vector3 {
    // TODO: Implement this method
    return new Vector3(
      this.x * scalar,
      this.y * scalar,
      this.z * scalar
    );
  }

  /**
   * Divide this vector by a scalar
   */
  divide(scalar: number): Vector3 {
    if (scalar === 0) {
      console.warn('Vector3.divide: Division by zero, returning clone');
      return this.clone();
    }
    // TODO: Implement the rest of this method
    return new Vector3(
      this.x / scalar,
      this.y / scalar,
      this.z / scalar
    );
  }

  // ============================================
  // VECTOR OPERATIONS
  // ============================================

  /**
   * Calculate the dot product with another vector
   */
  dot(other: Vector3): number {
    // TODO: Implement this method
    return this.x * other.x + this.y * other.y + this.z * other.z;
  }

  /**
   * Calculate the cross product with another vector
   * The cross product gives a vector perpendicular to both input vectors
   *
   * @param other - The other vector
   * @returns A new vector perpendicular to both
   */
  cross(other: Vector3): Vector3 {
    // TODO: Implement this method
    // Formula:
    // x = y1*z2 - z1*y2
    // y = z1*x2 - x1*z2
    // z = x1*y2 - y1*x2
    return new Vector3(
      this.y * other.z - this.z * other.y,
      this.z * other.x - this.x * other.z,
      this.x * other.y - this.y * other.x
    );
  }

  /**
   * Calculate the magnitude (length) of this vector
   */
  magnitude(): number {
    // TODO: Implement this method
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
  }

  /**
   * Calculate the squared magnitude
   */
  magnitudeSquared(): number {
    // TODO: Implement this method
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  /**
   * Get a normalized (unit) vector
   */
  normalize(): Vector3 {
    const mag = this.magnitude();
    if (mag === 0) {
      return Vector3.zero();
    }
    // TODO: Implement the rest of this method
    return this.divide(mag);
  }

  /**
   * Calculate distance to another point
   */
  distance(other: Vector3): number {
    // TODO: Implement this method
    return this.subtract(other).magnitude();
  }

  /**
   * Calculate squared distance to another point
   */
  distanceSquared(other: Vector3): number {
    // TODO: Implement this method
    return this.subtract(other).magnitudeSquared();
  }

  /**
   * Linear interpolation between this vector and another
   */
  lerp(other: Vector3, t: number): Vector3 {
    // TODO: Implement this method
    return new Vector3(
      this.x + (other.x - this.x) * t,
      this.y + (other.y - this.y) * t,
      this.z + (other.z - this.z) * t
    );
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Create a copy of this vector
   */
  clone(): Vector3 {
    // TODO: Implement this method
    return new Vector3(this.x, this.y, this.z);
  }

  /**
   * Check if this vector equals another
   */
  equals(other: Vector3): boolean {
    // TODO: Implement this method
    return (
      Math.abs(this.x - other.x) < Vector3.EPSILON &&
      Math.abs(this.y - other.y) < Vector3.EPSILON &&
      Math.abs(this.z - other.z) < Vector3.EPSILON
    );
  }

  /**
   * Convert to string for debugging
   */
  toString(): string {
    // TODO: Implement this method
    return `(${this.x}, ${this.y}, ${this.z})`;
  }

  // ============================================
  // ISOMETRIC HELPERS
  // These are specific to isometric games
  // ============================================

  /**
   * Get the "ground" position (Z = 0)
   * Useful for shadow rendering or ground-level calculations
   */
  groundPosition(): Vector3 {
    return new Vector3(this.x, this.y, 0);
  }

  /**
   * Add height to this position
   * @param height - Additional height to add
   */
  addHeight(height: number): Vector3 {
    return new Vector3(this.x, this.y, this.z + height);
  }

  /**
   * Get horizontal distance to another point (ignoring Z)
   */
  horizontalDistance(other: Vector3): number {
    const dx = this.x - other.x;
    const dy = this.y - other.y;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
